import React, { useState } from "react";

import Button from "components/Button";
import DebtorSearch from "components/DebtorSearchInput";
import Modal from "components/Modal";
import { FUNDING_REQUEST_CONTRACT_TYPES_MAP } from "helpers/constantTypes";
import { ICompany } from "models/HaulPay";

export default function SelfFinanceNoInvoiceToNonFactoredModal({
  isOpen,
  closeModal,
  onConfirm,
  onCloseModal,
  fromContractType,
  toContractType
}: Readonly<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: (debtor: ICompany) => void;
  onCloseModal?: () => void;
  fromContractType?: string;
  toContractType?: string;
}>) {
  const [selectedDebtor, setSelectedDebtor] = useState<ICompany | null>(null);

  const fromLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      fromContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? fromContractType;
  const toLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      toContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? toContractType;

  const handleCancel = () => {
    onCloseModal?.();
    closeModal();
  };

  const handleConfirm = () => {
    if (!selectedDebtor) return;

    onConfirm?.(selectedDebtor);
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      size="sm"
      wrapperClass="!my-auto desktop:!min-h-fit px-4 desktop:px-6"
      dialogClass="!bg-white dark:!bg-card !border !border-gray-200 dark:!border-card !p-0 rounded-2xl shadow-lg w-full"
      closeClass="!hidden">
      <div className="border-b border-gray-200 dark:border-white/10 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70">
          Contract Type Change
        </p>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white desktop:text-xl">
          {fromLabel} &rarr; {toLabel}
        </h2>
      </div>

      <div className="px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-sm leading-relaxed text-gray-600 dark:text-white/75">
          This funding request does not have a debtor. Select a debtor before changing the contract
          type.
        </p>

        <div className="mt-4">
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">Debtor</h6>
          <DebtorSearch
            name="debtor"
            required
            isConnectedDebtor={false}
            onSelect={(company) => setSelectedDebtor(company)}
            inputClass="!min-h-10 !rounded-lg !border !border-gray-200 dark:!border-white/10 !bg-white dark:!bg-card !px-3 !text-sm !text-gray-900 dark:!text-white"
            dropdownClass="!bg-white dark:!bg-card !border-gray-200 dark:!border-white/10"
            portalDropdown
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-white/10 px-4 py-4 desktop:flex-row desktop:justify-end desktop:gap-3 desktop:px-6">
        <Button
          type="button"
          rounded
          variant="outline"
          onClick={handleCancel}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[7rem] border-gray-300 dark:border-white/20 bg-transparent text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          onClick={handleConfirm}
          disabled={!selectedDebtor}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[10rem] !bg-primary dark:!bg-white !text-white dark:!text-card hover:!bg-primary/90 dark:hover:!bg-white/90">
          Confirm
        </Button>
      </div>
    </Modal>
  );
}
